import type { DonorDonation } from '@/types/donorFinancialYear'
import type { DonationSourceCategory } from '@/types/donationSource'

export type { DonationSourceCategory } from '@/types/donationSource'

const MANUAL_SOURCE_KEYS = new Set(['you', 'added_by_you', 'manual'])

export function isManualDonationSource(source: string | undefined): boolean {
  const normalised = source?.toLowerCase() ?? ''
  return MANUAL_SOURCE_KEYS.has(normalised)
}

/** Pill label: platform name (JustGiving) or “Added by you”. */
export function donationSourceLabel(donation: DonorDonation): string {
  if (isManualDonationSource(donation.source)) {
    return 'manual'
  }
  if (donation.source?.trim()) {
    return donation.source.trim()
  }
  if (donation.sourceCategory === 'contactless') {
    return 'Contactless'
  }
  if (donation.sourceCategory === 'swiftaid') {
    return 'Swiftaid'
  }
  return 'Swiftaid'
}

export function donationSourceCategory(
  donation: DonorDonation
): DonationSourceCategory {
  if (donation.sourceCategory) {
    return donation.sourceCategory
  }
  if (isManualDonationSource(donation.source)) {
    return 'manual'
  }
  const source = donation.source?.toLowerCase() ?? ''
  if (source === 'contactless') {
    return 'contactless'
  }
  if (source === 'swiftaid') {
    return 'swiftaid'
  }
  if (
    source.includes('justgiving') ||
    source.includes('give as you live') ||
    source.includes('enthuse')
  ) {
    return 'online'
  }
  return 'swiftaid'
}

/** CSS modifier for source pills (online / contactless / swiftaid / you). */
export function donationSourcePillClass(donation: DonorDonation): string {
  const category = donationSourceCategory(donation)
  if (category === 'manual') return 'sp-you'
  if (category === 'found-for-you') return 'sp-sw'
  if (category === 'online') return 'sp-online'
  if (category === 'contactless') return 'sp-contactless'
  return 'sp-sw'
}

/** i18n key for the donation detail modal source description body. */
export function donationSourceDetailBodyKey(donation: DonorDonation): string {
  const category = donationSourceCategory(donation)
  if (category === 'manual') return 'Dashboard.DonationDetailModal.Source.manual.Body'
  if (category === 'contactless') {
    return 'Dashboard.DonationDetailModal.Source.contactless.Body'
  }
  if (category === 'online') return 'Dashboard.DonationDetailModal.Source.online.Body'
  return 'Dashboard.DonationDetailModal.Source.swiftaid.Body'
}

/** i18n key for source title, or null when the platform name should be shown as-is. */
export function donationSourceDetailTitleKey(
  donation: DonorDonation
): string | null {
  const category = donationSourceCategory(donation)
  if (category === 'online' && donation.source?.trim()) {
    return null
  }
  return `Dashboard.DonationDetailModal.Source.${category}.Title`
}
